import {ApiUrls} from 'shared/config/model/urls';

import {createApi} from '@reduxjs/toolkit/query/react';
import httpBaseQuery from 'shared/api/rtk-query/httpBaseQuery';
import {AxiosRequestHeaders} from 'axios';

import IPagination from 'shared/api/model/IPagination';
import ICollectionParams from 'shared/api/model/ICollectionParams';
import {buildQuery} from 'shared/api/query';
import {providesList} from 'shared/api/rtk-query/RTKQueryHelper';
import IHydraResponse from 'shared/api/model/HydraResponse';
import {paginationAdapter} from 'shared/api/paginationHelper';

import {ILocation, ICreateLocationRequest, IUpdateLocationRequest} from '../model/ILocation';

export const locationAPI = createApi({
    reducerPath: 'locationAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['Location'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        createLocation: build.mutation<ILocation, ICreateLocationRequest>({
            query: (data) => ({
                path: ApiUrls.LOCATIONS(),
                method: 'POST',
                data,
            }),
            invalidatesTags: ['Location'],
        }),
        getLocations: build.query<IPagination<ILocation>, ICollectionParams>({
            query: ({page, sort, filters}) => ({
                path: buildQuery(ApiUrls.LOCATIONS(page, sort, filters)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesList(result?.items, 'Location'),
            transformResponse(
                rawResult: IHydraResponse<ILocation>,
            ): Promise<IPagination<ILocation>> | IPagination<ILocation> {
                return paginationAdapter(rawResult);
            },
        }),
        toggleLocationPublish: build.mutation<ILocation, { id: number; published: boolean }>({
            query: ({ id, published }) => ({
                path: ApiUrls.LOCATION(id),
                method: 'PUT',
                data: { published },
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Location', id },
                { type: 'Location', id: 'LIST' },
            ],
        }),
        updateLocation: build.mutation<ILocation, IUpdateLocationRequest>({
            query: ({id, ...data}) => ({
                path: ApiUrls.LOCATION(id),
                method: 'PUT',
                data,
            }),
            invalidatesTags: (_result, _error, { id }) => [
                { type: 'Location', id },
                { type: 'Location', id: 'LIST' },
            ],
        }),
        deleteLocation: build.mutation<void, number>({
            query: (id) => ({
                path: ApiUrls.LOCATION(id),
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: 'Location', id },
                { type: 'Location', id: 'LIST' },
            ],
        }),
        searchLocation: build.query<ILocation[], { cityId: number; query: string }>({
            query: ({ cityId, query }) => ({
                path: ApiUrls.SEARCH_LOCATIONS(cityId, query),
            }),
            providesTags: (result) =>
                result
                    ? [...result.map(({ id }) => ({ type: 'Location' as const, id })), 'Location']
                    : ['Location'],
        }),
    }),
});